import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import moment from 'moment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Box, Typography, Button } from '@mui/material';
import AddButton from './AddButton';
import { dispatch, useSelector } from '../../../../redux/store';
import { updateTransitionTime } from '../../../../redux/slices/lead';
import { useBoolean } from '../../../../hooks';
import {
    TabCount,
    TablePageNumber,
    TablePerPage,
    TableSearch,
    TableTreatment,
    TableFilterDate,
    TableStatus,
    TableFilterTags,
    TableOrder,
    TableOrderBy,
    TableReferral,
    TableCategory,
    TableBookedWith,
} from '../../../../utils/allTableTab';
import {
    datePickerBoxStyle,
    datePickerTextFieldStyle,
    dateTimeTypoStyle,
    tableCellRelativeBoxStyle,
} from '../../../../styles/NewLeadTableRowStyle';

const StartedDateField = ({
    rowData: rd,
    showTransitionDate,
    updateLeadPage,
    reloadCounter,
    tableType,
    getTransitionDate,
}) => {
    const rowData = { ...rd, id: Number(rd?.id?.toString()?.split("-")?.[0]) };
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { consultationDateFormat } = useSelector((state) => state.lead);
    const { enqueueSnackbar } = useSnackbar();
    const filteredTreatmentData = rowData?.treatmentData?.find((ele) => ele?.id === rowData?.treatment_id);

    // The picker must OPEN on the date that is currently saved, not on today.
    //
    // This used to hold a display string ('Tue, 8 September at 12:32 PM'), which
    // the app's AdapterDateFns cannot parse, so the calendar fell back to the
    // current month with nothing selected — the user then had to find the
    // existing value themselves before changing it. The adapter needs a JS Date.
    //
    // Parsed with the wire format the API actually sends ('DD-MM-YYYY HH:mm:ss'),
    // not 'DD/MM/YYYY hh:mm:ss': the separators differ and hh is 12-hour, so an
    // 18:17 start parsed as hh silently became 06:17.
    //
    // Memoise on the raw timestamp STRING, never on rowData or a Date. rowData
    // is rebuilt on every render, and a fresh Date object is a new identity each
    // time — either as a dependency would re-run the effect below on every
    // render and set state in a loop. getTransitionDate returns a string, and
    // so does the fallback, so this dep is a stable primitive.
    const rawStart =
        getTransitionDate(filteredTreatmentData?.state_transition_logs || []) ||
        rowData?.treatmentData?.[0]?.state_transition_logs?.[0]?.timestamp ||
        '';

    const savedStartMs = useMemo(() => {
        if (!rawStart) return null;
        const m = moment(rawStart, 'DD-MM-YYYY HH:mm:ss');
        return m.isValid() ? m.valueOf() : null;
    }, [rawStart]);

    const [selectedDate, setSelectedDate] = useState(
        savedStartMs ? new Date(savedStartMs) : null
    );

    // Re-seed when the grid reloads with a different value — e.g. after a save
    // made from the Treatment In Progress sidebar — so the two surfaces agree.
    useEffect(() => {
        setSelectedDate(savedStartMs ? new Date(savedStartMs) : null);
    }, [savedStartMs]);
    const openCalendar = useBoolean();
    const perPage = TablePerPage(tableType);
    const pageNumber = TablePageNumber(tableType);
    const status = TableStatus(tableType);
    const tableOrder = TableOrder(tableType);
    const tableOrderBy = TableOrderBy(tableType);
    const searchValue = TableSearch(tableType);
    const treatmentValue = TableTreatment(tableType);
    const searchFilterDate = TableFilterDate(tableType);
    const startDate = Array.isArray(searchFilterDate) ? searchFilterDate[0]?.startDate : null;
    const endDate = Array.isArray(searchFilterDate) ? searchFilterDate[0]?.endDate : null;
    const contactCount = TabCount(tableType);
    const searchTagsValue = TableFilterTags(tableType)?.toString();
    const referral = TableReferral(tableType);
    const category = TableCategory(tableType);
    const bookedWith = TableBookedWith(tableType);
    const treatmentData = [];
    treatmentValue?.map((value) => treatmentData.push(value.id));

    const handleDateChange = (date) => {
        setSelectedDate(date);
        const detail = {
            timestamp: `${moment(date).format('DD-MM-YYYY HH:mm:ss')}.0Z`,
            lead: rowData?.id,
            id: filteredTreatmentData?.id || rowData?.treatmentData?.[0]?.id,
            state: filteredTreatmentData?.status || rowData?.treatmentData?.[0]?.status,
        };
        dispatch(updateTransitionTime(detail, practiceId, handleSuccess));
    };

    const handleSuccess = (data) => {
        openCalendar.onFalse();
        enqueueSnackbar(data.message || '', {
            variant: data.success ? 'success' : 'error',
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'center',
            },
        });
        const reloadPayLoad = {
            limit: perPage,
            offset: pageNumber,
            filterStatus: status,
            order: tableOrder,
            orderId: tableOrderBy,
            searchQuery: searchValue,
            treatment: treatmentData?.toString(),
            startDate,
            endDate,
            contactedCount: contactCount,
            tags: searchTagsValue,
            referral,
            category,
            bookedWith,
        };
        updateLeadPage(reloadPayLoad);
        reloadCounter(reloadPayLoad);
    };

    const handleClick = () => {
        if (filteredTreatmentData?.state_transition_logs?.[0]?.timestamp) {
            openCalendar.onTrue();
        }
    };

    return (
        <Box sx={tableCellRelativeBoxStyle} onClick={handleClick}>
            {showTransitionDate(consultationDateFormat, rowData) ? (
                <Button sx={{ px: 0, fontWeight: '400', color: 'text.primary', '&:hover': { background: 'none' } }} disableRipple>
                    <Box>
                        <Typography
                            variant='05 Others/Label'
                            dangerouslySetInnerHTML={{ __html: showTransitionDate(consultationDateFormat, rowData) }}
                            sx={dateTimeTypoStyle}
                        />
                    </Box>
                </Button>
            ) : (
                <AddButton />
            )}
            <Box sx={datePickerBoxStyle}>
                <DateTimePicker
                    orientation='landscape'
                    closeOnSelect={false}
                    label=''
                    open={openCalendar.value}
                    onClose={openCalendar.onFalse}
                    name='DueDatetime'
                    value={selectedDate}
                    maxDateTime={new Date()}
                    // `format`, not `inputFormat`: this picker comes from
                    // @mui/x-date-pickers v6, which renamed the prop. The old
                    // name is silently ignored, so the field fell back to the
                    // adapter's default en-US mask and rendered 1 Aug as
                    // 08/01/2026.
                    format="dd/MM/yyyy HH:mm"
                    onAccept={(newValue) => handleDateChange(newValue)}
                    ampm={false}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            margin: 'normal',
                            sx: datePickerTextFieldStyle,
                            onKeyDown: (e) => e.preventDefault(),
                        },
                        actionBar: { actions: ['cancel', 'accept'] },
                    }}
                />
            </Box>
        </Box>
    );
};

StartedDateField.propTypes = {
    rowData: PropTypes.object,
    showTransitionDate: PropTypes.func,
    updateLeadPage: PropTypes.func,
    reloadCounter: PropTypes.func,
    tableType: PropTypes.string,
    getTransitionDate: PropTypes.func,
};

export default StartedDateField;
